/**
 * CreateMigrationJobWizard — multi-step wizard for kicking off a migration.
 *
 * Spec §15.2.2 (Migration Recipes) + §16.13.
 *
 * Five recipe cards are surfaced; the underlying source_type the wizard
 * submits is set per-recipe along with sensible defaults (canary ON,
 * quiet mode ON for self-serve, polite throttling for live sources).
 *
 * Recipes that depend on an adapter not yet shipped (anything other than
 * MSG today) are visible-but-disabled with a tooltip — keeps the menu
 * visible while preventing the runner's "source_type not yet implemented"
 * error from reaching real users.
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { useMailboxList } from '../../hooks/useMailboxes';
import { useCreateMigrationJob } from '../../hooks/useMigrationJobs';
import { ResumableUploadZone } from '../uploads/ResumableUploadZone';
import { EdgeMailAdminAPI } from '../../services/EdgeMailAdminAPI';
import type {
  MigrationJobOptions,
  MigrationSourceType,
  ImapSourceFolder,
  ImapSourceMessage,
  ImapMessageSelector,
  ImapParticipantField,
  ImapParticipantFilter,
  UploadSession,
} from '../../types/migration.types';

interface Recipe {
  id: string;
  source_type: MigrationSourceType;
  label: string;
  description: string;
  /** Whether the underlying SourceAdapter is shipped today. */
  enabled: boolean;
  pendingPr?: string;
  /** Override defaults applied to the canonical options template. */
  optionOverrides: Partial<MigrationJobOptions>;
  /** UI hint for the source-locator step. */
  locatorHint: 'msg-files' | 'eml-files' | 'pst-upload' | 'takeout-zip' | 'gmail-oauth' | 'imap-credentials';
}

const RECIPES: Recipe[] = [
  {
    id: 'msg-drag-drop',
    source_type: 'msg',
    label: 'Outlook MSG files',
    description: 'Drag-and-drop one or more .msg files exported from Outlook. The fastest path to verify a migration end-to-end.',
    enabled: true,
    optionOverrides: { canary_only: false, quiet_mode: true, sink: 'direct' },
    locatorHint: 'msg-files',
  },
  {
    id: 'outlook-pst',
    source_type: 'pst',
    label: 'My Outlook archive (.pst / .ost)',
    description: 'Bulk-import a Personal Folders file. Recommended for archives larger than a few hundred messages.',
    enabled: true,
    optionOverrides: { canary_only: true, canary_size: 1000, quiet_mode: true, sink: 'direct', include_deleted: false },
    locatorHint: 'pst-upload',
  },
  {
    id: 'eml-drag-drop',
    source_type: 'eml',
    label: 'EML files (RFC 5322)',
    description: 'Drag-and-drop one or more .eml files. Useful for forensic exports from any IMAP-based mail client.',
    enabled: true,
    optionOverrides: { canary_only: false, quiet_mode: true, sink: 'direct' },
    locatorHint: 'eml-files',
  },
  {
    id: 'gmail-takeout',
    source_type: 'takeout_mbox',
    label: 'My Gmail (Takeout zip)',
    description: 'Upload a Google Takeout export. Preserves labels via the precedence ladder.',
    enabled: false,
    pendingPr: 'Phase 2',
    optionOverrides: { canary_only: true, canary_size: 1000, quiet_mode: true, sink: 'direct', label_strategy: 'precedence_ladder' },
    locatorHint: 'takeout-zip',
  },
  {
    id: 'gmail-live',
    source_type: 'gmail_api',
    label: 'My Gmail (live, no download)',
    description: 'Connect via Google OAuth and import directly. Throttled to be polite; no download required.',
    enabled: false,
    pendingPr: 'Phase 2',
    optionOverrides: { canary_only: true, canary_size: 1000, quiet_mode: true, sink: 'direct' },
    locatorHint: 'gmail-oauth',
  },
  {
    id: 'imap-other',
    source_type: 'imap',
    label: 'Other IMAP server',
    description: 'Generic IMAP source. Provide host + credentials, then choose exactly which source folders to import.',
    enabled: true,
    optionOverrides: {
      canary_only: true,
      canary_size: 1000,
      quiet_mode: true,
      sink: 'direct',
      folder_target_mode: 'merge_with_mailbox',
      folder_strategy: 'preserve_hierarchy',
      recovery_mode: true,
    },
    locatorHint: 'imap-credentials',
  },
];

const DEFAULT_OPTIONS: MigrationJobOptions = {
  include_deleted: false,
  default_charset: 'windows-1252',
  charset_confidence_floor: 0.7,
  dedup_mode: 'strict',
  matter_link_rules: null,
  label_strategy: 'precedence_ladder',
  folder_strategy: 'preserve_hierarchy',
  folder_target_mode: 'migrated_root',
  migrated_root_folder_name: 'Migrated Emails',
  folder_path_map: undefined,
  recovery_mode: false,
  sink: 'direct',
  canary_only: false,
  canary_size: 1000,
  quiet_mode: true,
  run_classifier: false,
  default_privilege: 'non_privileged',
  since_date: null,
  until_date: null,
  source_retention_days: 90,
};

type WizardStep = 'recipe' | 'mailbox' | 'source' | 'options' | 'confirm';

interface CreateMigrationJobWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CreateMigrationJobWizard({ isOpen, onClose, onSuccess }: CreateMigrationJobWizardProps) {
  const [step, setStep] = useState<WizardStep>('recipe');
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [targetMailboxId, setTargetMailboxId] = useState<string>('');
  // PR 20: finalized resumable-upload sessions whose `finalized_path` becomes
  // the source_locator on job creation.
  const [uploads, setUploads] = useState<UploadSession[]>([]);
  // MSG recipe: how many separate upload zones to render. Each completed zone
  // pushes a session into `uploads`. PST/OST/MBOX/ZIP recipes use one zone.
  const [msgZoneCount, setMsgZoneCount] = useState(1);
  const [options, setOptions] = useState<MigrationJobOptions>(DEFAULT_OPTIONS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // PR 24 — IMAP credential form state.
  const [imapForm, setImapForm] = useState<{ host: string; port: string; useTls: boolean; username: string; password: string; rejectUnauthorized: boolean }>({
    host: '', port: '993', useTls: true, username: '', password: '', rejectUnauthorized: true,
  });
  const [imapCredentialId, setImapCredentialId] = useState<string | null>(null);
  const [imapCredError, setImapCredError] = useState<string | null>(null);
  const [imapCredBusy, setImapCredBusy] = useState(false);
  const [imapTestResult, setImapTestResult] = useState<{ folder_count: number; host: string; port: number } | null>(null);
  const [imapFolders, setImapFolders] = useState<ImapSourceFolder[]>([]);
  const [imapFolderError, setImapFolderError] = useState<string | null>(null);
  const [imapFolderBusy, setImapFolderBusy] = useState(false);
  const [selectedImapFolders, setSelectedImapFolders] = useState<string[]>([]);
  const [imapFolderTargets, setImapFolderTargets] = useState<Record<string, string>>({});
  const [imapMessagesByFolder, setImapMessagesByFolder] = useState<Record<string, ImapSourceMessage[]>>({});
  const [imapMessageBusyByFolder, setImapMessageBusyByFolder] = useState<Record<string, boolean>>({});
  const [imapMessageErrorByFolder, setImapMessageErrorByFolder] = useState<Record<string, string | null>>({});
  const [selectedImapMessageKeys, setSelectedImapMessageKeys] = useState<string[]>([]);
  const [imapRecoveryMode, setImapRecoveryMode] = useState<'folders' | 'participant'>('folders');
  const [imapParticipantMode, setImapParticipantMode] = useState<'email' | 'domain'>('email');
  const [imapParticipantValue, setImapParticipantValue] = useState('');
  const [imapParticipantFields, setImapParticipantFields] = useState<ImapParticipantField[]>(['from', 'to', 'cc', 'bcc']);

  const { data: mailboxList } = useMailboxList({ status: 'active', sortBy: 'email', sortOrder: 'asc', page: 1, pageSize: 100 });
  const createMutation = useCreateMigrationJob();
  const selectedImapMessages = useMemo(() => {
    const selected = new Set(selectedImapMessageKeys);
    return Object.values(imapMessagesByFolder)
      .flat()
      .filter((message) => selected.has(messageKey(message)));
  }, [imapMessagesByFolder, selectedImapMessageKeys]);
  const imapParticipantFilter = useMemo<ImapParticipantFilter | null>(() => {
    const value = imapParticipantValue.trim().toLowerCase().replace(/^@/, '');
    if (imapRecoveryMode !== 'participant' || !value || imapParticipantFields.length === 0) return null;
    return {
      mode: imapParticipantMode,
      value,
      fields: imapParticipantFields,
    };
  }, [imapParticipantFields, imapParticipantMode, imapParticipantValue, imapRecoveryMode]);

  const steps: Array<{ id: WizardStep; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'recipe', label: 'Source', icon: ArrowUpTrayIcon },
    { id: 'mailbox', label: 'Mailbox', icon: EnvelopeIcon },
    { id: 'source', label: 'Locator', icon: Squares2X2Icon },
    { id: 'options', label: 'Options', icon: CogIcon },
    { id: 'confirm', label: 'Confirm', icon: CheckCircleIcon },
  ];
  const currentStepIndex = steps.findIndex((s) => s.id === step);

  const canProceed = useMemo(() => {
    switch (step) {
      case 'recipe': return !!recipe && recipe.enabled;
      case 'mailbox': return !!targetMailboxId;
      case 'source':
        if (!recipe) return false;
        if (recipe.locatorHint === 'msg-files') return uploads.length > 0;
        if (recipe.locatorHint === 'eml-files') return uploads.length > 0;
        if (recipe.locatorHint === 'pst-upload') return uploads.length === 1;
        if (recipe.locatorHint === 'imap-credentials') {
          if (!imapCredentialId || selectedImapFolders.length === 0) return false;
          if (imapRecoveryMode !== 'participant') return true;
          return !!imapParticipantFilter;
        }
        return false; // gmail recipes still ship later
      case 'options': return true;
      case 'confirm': return true;
      default: return false;
    }
  }, [step, recipe, targetMailboxId, uploads, imapCredentialId, selectedImapFolders.length, imapRecoveryMode, imapParticipantFilter]);

  const handleNext = useCallback(() => {
    const i = currentStepIndex + 1;
    if (i < steps.length) setStep(steps[i].id);
  }, [currentStepIndex, steps]);

  const handleBack = useCallback(() => {
    const i = currentStepIndex - 1;
    if (i >= 0) setStep(steps[i].id);
  }, [currentStepIndex, steps]);

  const handleRecipeSelect = useCallback((r: Recipe) => {
    setRecipe(r);
    setOptions({ ...DEFAULT_OPTIONS, ...r.optionOverrides });
    setSelectedImapFolders([]);
    setImapFolderTargets({});
    setImapMessagesByFolder({});
    setImapMessageBusyByFolder({});
    setImapMessageErrorByFolder({});
    setSelectedImapMessageKeys([]);
    setImapRecoveryMode('folders');
    setImapParticipantMode('email');
    setImapParticipantValue('');
    setImapParticipantFields(['from', 'to', 'cc', 'bcc']);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!recipe) return;
    setSubmitError(null);

    // Translate finalized upload sessions into the source_locator shape
    // each adapter expects: MSG/MBOX/ZIP take a list of paths under
    // `uploads`; PST/OST take a single `upload`.
    const finalizedPaths = uploads
      .map((u) => u.finalized_path)
      .filter((p): p is string => !!p);
    const imapFolderPathMap = imapParticipantFilter
      ? {}
      : Object.fromEntries(
        selectedImapFolders.map((path) => [path, normalizeFolderPath(imapFolderTargets[path] || path)]),
      );
    const imapMessageSelectors: ImapMessageSelector[] = selectedImapMessages.map((message) => ({
      folder: message.folder,
      raw_folder: message.raw_folder,
      uid: message.uid,
      uidvalidity: message.uidvalidity,
      message_id: message.message_id,
    }));
    const submitOptions: MigrationJobOptions = recipe.locatorHint === 'imap-credentials'
      ? {
        ...options,
        ...(imapParticipantFilter ? {} : { folder_path_map: imapFolderPathMap }),
        recovery_mode: options.recovery_mode ?? true,
      }
      : options;
    const sourceLocator: Record<string, unknown> =
      recipe.locatorHint === 'msg-files'
        ? { uploads: finalizedPaths }
        : recipe.locatorHint === 'eml-files'
          ? { uploads: finalizedPaths }
          : recipe.locatorHint === 'pst-upload'
            ? { upload: finalizedPaths[0] }
            : recipe.locatorHint === 'takeout-zip'
              ? { upload: finalizedPaths[0] }
              : recipe.locatorHint === 'imap-credentials' && imapCredentialId
                ? {
                  credential_id: imapCredentialId,
                  reject_unauthorized: imapForm.rejectUnauthorized,
                  folders: selectedImapFolders,
                  ...(!imapParticipantFilter && imapMessageSelectors.length > 0 ? { message_selectors: imapMessageSelectors } : {}),
                  ...(imapParticipantFilter ? { participant_filter: imapParticipantFilter } : {}),
                }
                : {};

    try {
      await createMutation.mutateAsync({
        target_mailbox_id: targetMailboxId,
        source_type: recipe.source_type,
        source_locator: sourceLocator,
        options: submitOptions,
      });
      onSuccess?.();
      handleClose();
    } catch (err) {
      const msg = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Failed to create migration job';
      setSubmitError(msg);
    }
  }, [recipe, targetMailboxId, uploads, selectedImapFolders, selectedImapMessages, imapFolderTargets, options, createMutation, onSuccess, imapCredentialId, imapForm.rejectUnauthorized, imapParticipantFilter]);

  const handleClose = useCallback(() => {
    setStep('recipe');
    setRecipe(null);
    setTargetMailboxId('');
    setUploads([]);
    setMsgZoneCount(1);
    setOptions(DEFAULT_OPTIONS);
    setSubmitError(null);
    setImapForm({ host: '', port: '993', useTls: true, username: '', password: '', rejectUnauthorized: true });
    setImapCredentialId(null);
    setImapCredError(null);
    setImapCredBusy(false);
    setImapTestResult(null);
    setImapFolders([]);
    setImapFolderError(null);
    setImapFolderBusy(false);
    setSelectedImapFolders([]);
    setImapFolderTargets({});
    setImapMessagesByFolder({});
    setImapMessageBusyByFolder({});
    setImapMessageErrorByFolder({});
    setSelectedImapMessageKeys([]);
    setImapRecoveryMode('folders');
    setImapParticipantMode('email');
    setImapParticipantValue('');
    setImapParticipantFields(['from', 'to', 'cc', 'bcc']);
    onClose();
  }, [onClose]);

  // PR 24 — stage IMAP credentials. Encrypted server-side; we keep just the credential_id.
  const handleStageImapCredential = useCallback(async () => {
    setImapCredError(null);
    setImapCredBusy(true);
    try {
      const portNum = parseInt(imapForm.port, 10);
      if (!imapForm.host.trim()) throw new Error('Host is required');
      if (!Number.isFinite(portNum) || portNum <= 0 || portNum > 65535) throw new Error('Port must be 1–65535');
      if (!imapForm.username.trim()) throw new Error('Username is required');
      if (!imapForm.password) throw new Error('Password is required');

      const result = await EdgeMailAdminAPI.createMigrationCredential({
        kind: 'imap_password',
        host: imapForm.host.trim(),
        port: portNum,
        use_tls: imapForm.useTls,
        username: imapForm.username.trim(),
        secret: imapForm.password,
        reject_unauthorized: imapForm.rejectUnauthorized,
      });
      setImapCredentialId(result.credential_id);
      setImapTestResult(result.connection_test);
      setImapFolderBusy(true);
      setImapFolderError(null);
      try {
        const folders = await EdgeMailAdminAPI.listMigrationCredentialFolders(result.credential_id, {
          reject_unauthorized: imapForm.rejectUnauthorized,
        });
        setImapFolders(folders);
        const selectable = folders.filter((folder) => folder.selectable);
        setSelectedImapFolders([]);
        setImapFolderTargets(Object.fromEntries(selectable.map((folder) => [folder.path, normalizeFolderPath(folder.path)])));
        setImapMessagesByFolder({});
        setImapMessageBusyByFolder({});
        setImapMessageErrorByFolder({});
        setSelectedImapMessageKeys([]);
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiMsg = (err as any)?.response?.data?.error?.message;
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as Error).message)
          : 'Failed to list source folders';
        setImapFolderError(apiMsg || msg);
      } finally {
        setImapFolderBusy(false);
      }
      // Wipe the password from local state once we have the credential_id —
      // the wizard never needs to see it again.
      setImapForm((f) => ({ ...f, password: '' }));
    } catch (err) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as Error).message)
        : 'Failed to stage credentials';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiMsg = (err as any)?.response?.data?.error?.message;
      setImapCredError(apiMsg || msg);
    } finally {
      setImapCredBusy(false);
    }
  }, [imapForm.host, imapForm.port, imapForm.username, imapForm.password, imapForm.useTls, imapForm.rejectUnauthorized]);

  const loadImapFolderMessages = useCallback(async (folderPath: string) => {
    if (!imapCredentialId) return;
    setImapMessageBusyByFolder((prev) => ({ ...prev, [folderPath]: true }));
    setImapMessageErrorByFolder((prev) => ({ ...prev, [folderPath]: null }));
    try {
      const result = await EdgeMailAdminAPI.listMigrationCredentialFolderMessages(imapCredentialId, {
        folder: folderPath,
        reject_unauthorized: imapForm.rejectUnauthorized,
        limit: 500,
      });
      setImapMessagesByFolder((prev) => ({ ...prev, [folderPath]: result.messages }));
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiMsg = (err as any)?.response?.data?.error?.message;
      const msg = err && typeof err === 'object' && 'message' in err
        ? String((err as Error).message)
        : 'Failed to preview source messages';
      setImapMessageErrorByFolder((prev) => ({ ...prev, [folderPath]: apiMsg || msg }));
    } finally {
      setImapMessageBusyByFolder((prev) => ({ ...prev, [folderPath]: false }));
    }
  }, [imapCredentialId, imapForm.rejectUnauthorized]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ArrowUpTrayIcon className="h-5 w-5 text-lex-accent" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create Migration Job</h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-4 px-6 py-3 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === currentStepIndex;
            const isDone = i < currentStepIndex;
            return (
              <div key={s.id} className="flex items-center gap-2 text-sm whitespace-nowrap">
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-lex-accent text-white' :
                    isDone ? 'bg-emerald-500 text-white' :
                    'bg-gray-200 dark:bg-gray-700 text-gray-500'
                  }`}
                >
                  {isDone ? <CheckCircleIcon className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <span className={isActive ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-500'}>
                  {s.label}
                </span>
                {i < steps.length - 1 && <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-700" />}
              </div>
            );
          })}
        </div>

        {/* Step body */}
        <div className="flex-1 overflow-auto p-6">
          {step === 'recipe' && (
            <div className="space-y-3">
              {RECIPES.map((r) => (
                <button
                  key={r.id}
                  onClick={() => r.enabled && handleRecipeSelect(r)}
                  disabled={!r.enabled}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    !r.enabled
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 cursor-not-allowed opacity-60'
                      : recipe?.id === r.id
                        ? 'border-lex-accent bg-lex-accent-soft'
                        : 'border-gray-200 dark:border-gray-700 hover:border-lex-accent'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">{r.label}</div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{r.description}</p>
                    </div>
                    {!r.enabled && r.pendingPr && (
                      <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                        {r.pendingPr}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 'mailbox' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target mailbox
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Imported messages land in this mailbox. Tenant boundary is enforced server-side — only mailboxes in your tenant are listed.
              </p>
              <select
                value={targetMailboxId}
                onChange={(e) => setTargetMailboxId(e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              >
                <option value="">— Select a mailbox —</option>
                {(mailboxList?.mailboxes || []).map((mb) => (
                  <option key={mb.mailbox_id} value={mb.mailbox_id}>
                    {mb.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {step === 'source' && recipe && (
            <div className="space-y-3">
              {recipe.locatorHint === 'msg-files' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Upload MSG files
                  </label>
                  <p className="text-xs text-gray-500">
                    Drop one or more .msg files. Each is uploaded with a resumable session — a refresh or network blip will pick up where it left off.
                  </p>
                  {Array.from({ length: msgZoneCount }).map((_, i) => (
                    <ResumableUploadZone
                      key={i}
                      kind="msg"
                      accept=".msg,application/vnd.ms-outlook"
                      onComplete={(s) => setUploads((prev) => prev.some((p) => p.session_id === s.session_id) ? prev : [...prev, s])}
                      onClear={() => {
                        setUploads((prev) => prev.filter((_, idx) => idx !== i));
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setMsgZoneCount((n) => n + 1)}
                    className="text-xs text-lex-accent hover:underline"
                  >
                    + Add another MSG file
                  </button>
                </>
              )}
              {recipe.locatorHint === 'eml-files' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Upload EML files
                  </label>
                  <p className="text-xs text-gray-500">
                    Drop one or more .eml files. Each is uploaded with a resumable session — a refresh or network blip will pick up where it left off.
                  </p>
                  {Array.from({ length: msgZoneCount }).map((_, i) => (
                    <ResumableUploadZone
                      key={i}
                      kind="eml"
                      accept=".eml,message/rfc822"
                      onComplete={(s) => setUploads((prev) => prev.some((p) => p.session_id === s.session_id) ? prev : [...prev, s])}
                      onClear={() => {
                        setUploads((prev) => prev.filter((_, idx) => idx !== i));
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setMsgZoneCount((n) => n + 1)}
                    className="text-xs text-lex-accent hover:underline"
                  >
                    + Add another EML file
                  </button>
                </>
              )}
              {recipe.locatorHint === 'pst-upload' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Upload Outlook archive (.pst / .ost)
                  </label>
                  <p className="text-xs text-gray-500">
                    PST archives can be tens of gigabytes. The uploader chunks the file and resumes automatically if the connection drops.
                  </p>
                  <ResumableUploadZone
                    kind="pst"
                    accept=".pst,.ost,application/vnd.ms-outlook,application/octet-stream"
                    helpText="Up to 100 GB. Chunked + resumable."
                    onComplete={(s) => setUploads([s])}
                    onClear={() => setUploads([])}
                  />
                </>
              )}
              {recipe.locatorHint === 'imap-credentials' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    IMAP server credentials
                  </label>
                  <p className="text-xs text-gray-500">
                    Credentials are encrypted (AES-256-GCM) before being persisted. The plaintext password never leaves this dialog and is purged when the migration completes.
                  </p>

                  {imapCredentialId ? (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3 flex items-start gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-emerald-900 dark:text-emerald-100 truncate">
                          ✅ Connected to {imapForm.host}:{imapForm.port}
                        </div>
                        <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                          User <span className="font-mono">{imapForm.username}</span> · {imapForm.useTls ? 'TLS' : 'plaintext'}
                        </div>
                        {imapTestResult && (
                          <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1 font-medium">
                            Login verified · {imapTestResult.folder_count} folder(s) discovered.
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (imapCredentialId) void EdgeMailAdminAPI.deleteMigrationCredential(imapCredentialId).catch(() => undefined);
                          setImapCredentialId(null);
                          setImapTestResult(null);
                          setImapFolders([]);
                          setSelectedImapFolders([]);
                          setImapFolderTargets({});
                          setImapMessagesByFolder({});
                          setImapMessageBusyByFolder({});
                          setImapMessageErrorByFolder({});
                          setSelectedImapMessageKeys([]);
                          setImapFolderError(null);
                        }}
                        className="text-xs px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-700"
                      >
                        Re-enter
                      </button>
	                    </div>
	                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Host</label>
                          <input
                            type="text"
                            value={imapForm.host}
                            onChange={(e) => setImapForm((f) => ({ ...f, host: e.target.value }))}
                            placeholder="imap.example.com"
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Port</label>
                          <input
                            type="number"
                            min={1}
                            max={65535}
                            value={imapForm.port}
                            onChange={(e) => setImapForm((f) => ({ ...f, port: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Username</label>
                        <input
                          type="text"
                          value={imapForm.username}
                          onChange={(e) => setImapForm((f) => ({ ...f, username: e.target.value }))}
                          placeholder="alice@example.com"
                          autoComplete="username"
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Password / app password</label>
                        <input
                          type="password"
                          value={imapForm.password}
                          onChange={(e) => setImapForm((f) => ({ ...f, password: e.target.value }))}
                          autoComplete="new-password"
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          For Microsoft 365 / Gmail accounts with MFA, use an app-specific password.
                        </p>
                      </div>
                      <Toggle
                        label="Use TLS (port 993)"
                        hint="Almost always on. Only disable for legacy STARTTLS-on-143 servers."
                        value={imapForm.useTls}
                        onChange={(v) => setImapForm((f) => ({ ...f, useTls: v, port: v ? '993' : '143' }))}
                      />
                      <Toggle
                        label="Strict TLS certificate validation"
                        hint="Disable only for self-signed certificates on internal servers."
                        value={imapForm.rejectUnauthorized}
                        onChange={(v) => setImapForm((f) => ({ ...f, rejectUnauthorized: v }))}
                      />

                      {imapCredError && (
                        <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-2 rounded">
                          {imapCredError}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleStageImapCredential}
                        disabled={imapCredBusy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-lex-accent text-white hover:bg-lex-accent-strong disabled:opacity-40"
                      >
                        {imapCredBusy ? 'Staging…' : 'Stage credentials'}
                      </button>
	                    </div>
	                  )}
	                  {imapCredentialId && (
	                    <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        <div className="rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/40 p-3 space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {([
                              ['folders', 'Folders / exact emails'],
                              ['participant', 'Find one user or domain'],
                            ] as Array<[typeof imapRecoveryMode, string]>).map(([mode, label]) => (
                              <button
                                key={mode}
                                type="button"
                                onClick={() => {
                                  setImapRecoveryMode(mode);
                                  if (mode === 'participant') setSelectedImapMessageKeys([]);
                                }}
                                className={`rounded px-3 py-1.5 text-xs font-medium border ${
                                  imapRecoveryMode === mode
                                    ? 'border-lex-accent bg-lex-accent/10 text-lex-accent'
                                    : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          {imapRecoveryMode === 'participant' && (
                            <div className="space-y-2">
                              <div className="grid grid-cols-[120px_1fr] gap-2">
                                <select
                                  value={imapParticipantMode}
                                  onChange={(e) => setImapParticipantMode(e.target.value as 'email' | 'domain')}
                                  className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-xs text-gray-900 dark:text-white"
                                >
                                  <option value="email">Email</option>
                                  <option value="domain">Domain</option>
                                </select>
                                <input
                                  type="text"
                                  value={imapParticipantValue}
                                  onChange={(e) => setImapParticipantValue(e.target.value)}
                                  placeholder={imapParticipantMode === 'email' ? 'sarah@domain.com' : 'domain.com'}
                                  className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-xs text-gray-900 dark:text-white"
                                />
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[11px] text-gray-500 dark:text-gray-400">Search fields</span>
                                {(['from', 'to', 'cc', 'bcc'] as ImapParticipantField[]).map((field) => (
                                  <label key={field} className="inline-flex items-center gap-1 text-[11px] text-gray-700 dark:text-gray-300">
                                    <input
                                      type="checkbox"
                                      checked={imapParticipantFields.includes(field)}
                                      onChange={(e) => {
                                        setImapParticipantFields((prev) => {
                                          if (e.target.checked) return prev.includes(field) ? prev : [...prev, field];
                                          return prev.filter((existing) => existing !== field);
                                        });
                                      }}
                                    />
                                    {field.toUpperCase()}
                                  </label>
                                ))}
                              </div>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                EdgeMail will search every selected source folder on the IMAP server and import only messages whose envelope matches this participant.
                              </p>
                            </div>
                          )}
                        </div>
	                      <div className="flex items-start justify-between gap-3">
	                        <div>
	                          <div className="text-sm font-medium text-gray-900 dark:text-white">Select source folders</div>
	                          <p className="text-xs text-gray-500 dark:text-gray-400">
	                            {imapRecoveryMode === 'participant'
                                ? 'Check the folders EdgeMail should search for this user or domain.'
                                : 'Check folders to restore the whole folder, or preview a folder and check only specific emails.'}
	                          </p>
	                        </div>
	                        <button
	                          type="button"
	                          onClick={() => {
	                            const selectable = imapFolders.filter((folder) => folder.selectable).map((folder) => folder.path);
	                            setSelectedImapFolders(selectable);
	                            setImapFolderTargets((prev) => ({
	                              ...Object.fromEntries(selectable.map((path) => [path, normalizeFolderPath(path)])),
	                              ...prev,
	                            }));
	                          }}
	                          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
	                        >
	                          Select all
	                        </button>
	                      </div>
	                      {imapFolderBusy && (
	                        <div className="text-sm text-gray-500 dark:text-gray-400">Reading folders from source server...</div>
	                      )}
	                      {imapFolderError && (
	                        <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-2 rounded">
	                          {imapFolderError}
	                        </div>
	                      )}
	                      {!imapFolderBusy && imapFolders.length === 0 && (
	                        <div className="text-sm text-gray-500 dark:text-gray-400">No selectable folders were returned by the source server.</div>
	                      )}
	                      {imapFolders.length > 0 && (
	                        <div className="max-h-64 overflow-auto rounded border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
	                          {imapFolders.map((folder) => {
	                            const selected = selectedImapFolders.includes(folder.path);
	                            return (
	                              <div
	                                key={folder.path}
	                                className={`p-2 ${folder.selectable ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}
	                              >
                                  <div className="flex items-start gap-2">
                                    <input
                                      type="checkbox"
                                      className="mt-1"
                                      disabled={!folder.selectable}
                                      checked={selected}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedImapFolders((prev) => prev.includes(folder.path) ? prev : [...prev, folder.path]);
                                          setImapFolderTargets((prev) => ({ ...prev, [folder.path]: prev[folder.path] || normalizeFolderPath(folder.path) }));
                                        } else {
                                          setSelectedImapFolders((prev) => prev.filter((path) => path !== folder.path));
                                          setSelectedImapMessageKeys((prev) => prev.filter((key) => !key.startsWith(`${folder.path}|`)));
                                        }
                                      }}
                                    />
	                                  <div className="min-w-0 flex-1">
	                                    <div className="flex items-center justify-between gap-2">
	                                      <span className="truncate text-sm font-medium text-gray-900 dark:text-white">{folder.path}</span>
	                                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
	                                        {folder.exists ?? 0} messages
	                                      </span>
	                                    </div>
	                                    {folder.open_error && (
	                                      <div className="text-xs text-rose-600 dark:text-rose-300">{folder.open_error}</div>
	                                    )}
	                                    {selected && (
	                                      <div className="mt-2 grid grid-cols-[80px_1fr] items-center gap-2">
	                                        <span className="text-xs text-gray-500 dark:text-gray-400">Restore to</span>
	                                        <input
	                                          type="text"
	                                          value={imapFolderTargets[folder.path] ?? normalizeFolderPath(folder.path)}
	                                          onChange={(e) => setImapFolderTargets((prev) => ({ ...prev, [folder.path]: e.target.value }))}
	                                          className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs font-mono text-gray-900 dark:text-white"
	                                        />
	                                      </div>
	                                    )}
                                      {selected && imapRecoveryMode !== 'participant' && (
                                        <ImapMessagePicker
                                          folder={folder}
                                          messages={imapMessagesByFolder[folder.path] ?? []}
                                          busy={!!imapMessageBusyByFolder[folder.path]}
                                          error={imapMessageErrorByFolder[folder.path] ?? null}
                                          selectedKeys={selectedImapMessageKeys}
                                          onLoad={() => loadImapFolderMessages(folder.path)}
                                          onToggleMessage={(message, checked) => {
                                            const key = messageKey(message);
                                            setSelectedImapMessageKeys((prev) => {
                                              if (checked) return prev.includes(key) ? prev : [...prev, key];
                                              return prev.filter((existing) => existing !== key);
                                            });
                                          }}
                                          onSelectVisible={(messages) => {
                                            const keys = messages.map(messageKey);
                                            setSelectedImapMessageKeys((prev) => Array.from(new Set([...prev, ...keys])));
                                          }}
                                          onClearVisible={(messages) => {
                                            const keys = new Set(messages.map(messageKey));
                                            setSelectedImapMessageKeys((prev) => prev.filter((key) => !keys.has(key)));
                                          }}
                                        />
                                      )}
	                                  </div>
	                                </div>
	                              </div>
	                            );
	                          })}
	                        </div>
	                      )}
	                      <div className="text-xs text-gray-500 dark:text-gray-400">
	                        Selected {selectedImapFolders.length} folder(s)
                          {selectedImapMessageKeys.length > 0 ? ` · ${selectedImapMessageKeys.length} exact message(s)` : ''}.
	                      </div>
	                    </div>
	                  )}
	                </>
	              )}
              {recipe.locatorHint !== 'msg-files' && recipe.locatorHint !== 'pst-upload' && recipe.locatorHint !== 'eml-files' && recipe.locatorHint !== 'imap-credentials' && (
                <div className="text-sm text-gray-500">
                  Source-locator UI for this recipe ships in a later release.
                </div>
              )}
            </div>
          )}

          {step === 'options' && (
            <div className="space-y-4">
              <Toggle
                label="Quiet Mode"
                hint="When enabled, imported messages do not trigger new-mail notifications, badge increments, or browser pings."
                value={options.quiet_mode}
                onChange={(v) => setOptions({ ...options, quiet_mode: v })}
              />
              <Toggle
                label="Canary Mode"
                hint="When enabled, the first N messages land in a hidden Migration Preview folder for review before the full import resumes."
                value={options.canary_only}
                onChange={(v) => setOptions({ ...options, canary_only: v })}
              />
              {options.canary_only && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Canary sample size</label>
                  <input
                    type="number"
                    min={1}
                    max={100000}
                    value={options.canary_size}
                    onChange={(e) => setOptions({ ...options, canary_size: parseInt(e.target.value, 10) || 1000 })}
                    className="mt-1 w-32 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  />
                </div>
              )}
              {recipe?.source_type === 'imap' && (
                <Toggle
                  label="Recovery mode"
                  hint="If a selected source message already exists elsewhere in the target mailbox, move the existing message into the mapped recovery folder instead of importing a duplicate."
                  value={!!options.recovery_mode}
                  onChange={(v) => setOptions({ ...options, recovery_mode: v })}
                />
              )}
              <Toggle
                label="Include deleted items"
                hint="When the source contains soft-deleted items (Outlook 'Deleted Items', etc.), include them in the import."
                value={options.include_deleted}
                onChange={(v) => setOptions({ ...options, include_deleted: v })}
              />
              {(recipe?.locatorHint === 'pst-upload' || recipe?.source_type === 'pst' || recipe?.source_type === 'ost' || recipe?.source_type === 'msg' || recipe?.source_type === 'imap') && (
                <Toggle
                  label="Preserve folder hierarchy"
                  hint="Mirror the source's nested folder tree under the canonical EdgeMail roots (e.g. /Inbox/Clients/Acme → INBOX/Clients/Acme). Turn off to flatten everything to the canonical top-level folder only."
                  value={(options.folder_strategy ?? 'preserve_hierarchy') === 'preserve_hierarchy'}
                  onChange={(v) => setOptions({ ...options, folder_strategy: v ? 'preserve_hierarchy' : 'flatten_to_canonical' })}
                />
              )}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Target folder placement</label>
                <div className="grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setOptions({ ...options, folder_target_mode: 'merge_with_mailbox' })}
                    className={`text-left rounded-md border px-3 py-2 text-sm transition-colors ${
                      (options.folder_target_mode ?? 'migrated_root') === 'merge_with_mailbox'
                        ? 'border-lex-accent bg-lex-accent/10 text-gray-900 dark:text-white'
                        : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <span className="block font-medium">Mirror into mailbox</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Create or reuse folders in the target mailbox tree.</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOptions({ ...options, folder_target_mode: 'migrated_root' })}
                    className={`text-left rounded-md border px-3 py-2 text-sm transition-colors ${
                      (options.folder_target_mode ?? 'migrated_root') === 'migrated_root'
                        ? 'border-lex-accent bg-lex-accent/10 text-gray-900 dark:text-white'
                        : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <span className="block font-medium">Isolate migrated mail</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Place the source tree under a separate Migrated Emails folder.</span>
                  </button>
                </div>
                {(options.folder_target_mode ?? 'migrated_root') === 'migrated_root' && (
                  <input
                    type="text"
                    value={options.migrated_root_folder_name ?? 'Migrated Emails'}
                    onChange={(e) => setOptions({ ...options, migrated_root_folder_name: e.target.value || 'Migrated Emails' })}
                    className="w-full max-w-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Default charset (fallback)</label>
                <input
                  type="text"
                  value={options.default_charset}
                  onChange={(e) => setOptions({ ...options, default_charset: e.target.value })}
                  className="mt-1 w-48 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used when the source declares us-ascii but the body contains non-ASCII bytes. Defaults to windows-1252 (most common for English Outlook exports).
                </p>
              </div>
            </div>
          )}

          {step === 'confirm' && recipe && (
            <div className="space-y-3">
              <SummaryRow label="Recipe" value={recipe.label} />
              <SummaryRow label="Source type" value={recipe.source_type} />
              <SummaryRow label="Target mailbox" value={
                mailboxList?.mailboxes.find((m) => m.mailbox_id === targetMailboxId)?.email ?? targetMailboxId
              } />
              <SummaryRow label="Sink" value={options.sink} />
              <SummaryRow label="Canary mode" value={options.canary_only ? `On (${options.canary_size} sample)` : 'Off'} />
              <SummaryRow label="Quiet mode" value={options.quiet_mode ? 'On' : 'Off'} />
              <SummaryRow label="Include deleted" value={options.include_deleted ? 'Yes' : 'No'} />
              {(recipe.source_type === 'pst' || recipe.source_type === 'ost' || recipe.source_type === 'msg' || recipe.source_type === 'imap') && (
                <SummaryRow
                  label="Folder strategy"
                  value={
                    (options.folder_strategy ?? 'preserve_hierarchy') === 'preserve_hierarchy'
                      ? 'Preserve hierarchy'
                      : 'Flatten to canonical'
                  }
                />
              )}
              <SummaryRow
                label="Folder placement"
                value={
                  (options.folder_target_mode ?? 'migrated_root') === 'migrated_root'
                    ? `Isolated under ${options.migrated_root_folder_name ?? 'Migrated Emails'}`
                    : 'Mirror into target mailbox'
                }
              />
              {recipe.locatorHint === 'msg-files' && (
                <SummaryRow label="MSG files" value={`${uploads.length} file(s) uploaded`} />
              )}
              {recipe.locatorHint === 'eml-files' && (
                <SummaryRow label="EML files" value={`${uploads.length} file(s) uploaded`} />
              )}
              {recipe.locatorHint === 'pst-upload' && uploads[0] && (
                <SummaryRow
                  label="PST archive"
                  value={`${uploads[0].filename} (${(uploads[0].total_size / 1024 / 1024).toFixed(1)} MB)`}
                />
              )}
              {recipe.locatorHint === 'imap-credentials' && (
                <SummaryRow
                  label="IMAP server"
                  value={imapCredentialId
                    ? `${imapForm.username}@${imapForm.host}:${imapForm.port} ${imapForm.useTls ? '(TLS)' : '(plaintext)'}`
                    : '— credentials not staged'}
                />
              )}
              {recipe.locatorHint === 'imap-credentials' && (
                <SummaryRow
                  label="Source folders"
                  value={`${selectedImapFolders.length} selected`}
                />
              )}
              {recipe.locatorHint === 'imap-credentials' && imapParticipantFilter && (
                <SummaryRow
                  label="Participant recovery"
                  value={`${imapParticipantFilter.mode}: ${imapParticipantFilter.value} (${imapParticipantFilter.fields.join(', ')})`}
                />
              )}
              {recipe.locatorHint === 'imap-credentials' && selectedImapMessages.length > 0 && (
                <SummaryRow
                  label="Exact messages"
                  value={`${selectedImapMessages.length} selected`}
                />
              )}
              {recipe.locatorHint === 'imap-credentials' && (
                <SummaryRow
                  label="Recovery mode"
                  value={options.recovery_mode ? 'On - move existing duplicates into mapped folders' : 'Off - dedupe only'}
                />
              )}
              {submitError && (
                <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 rounded">
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleBack}
            disabled={currentStepIndex === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back
          </button>
          {step !== 'confirm' ? (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-lex-accent text-white text-sm font-medium hover:bg-lex-accent-strong disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-lex-accent text-white text-sm font-medium hover:bg-lex-accent-strong disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Creating…' : 'Create job'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tiny inline atoms ───────────────────────────────────────────────────
function Toggle({ label, hint, value, onChange }: { label: string; hint?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          value ? 'bg-lex-accent' : 'bg-gray-300 dark:bg-gray-700'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-32 text-gray-500 dark:text-gray-400">{label}</div>
      <div className="flex-1 text-gray-900 dark:text-white font-medium">{value}</div>
    </div>
  );
}

function ImapMessagePicker({
  folder,
  messages,
  busy,
  error,
  selectedKeys,
  onLoad,
  onToggleMessage,
  onSelectVisible,
  onClearVisible,
}: {
  folder: ImapSourceFolder;
  messages: ImapSourceMessage[];
  busy: boolean;
  error: string | null;
  selectedKeys: string[];
  onLoad: () => void;
  onToggleMessage: (message: ImapSourceMessage, checked: boolean) => void;
  onSelectVisible: (messages: ImapSourceMessage[]) => void;
  onClearVisible: (messages: ImapSourceMessage[]) => void;
}) {
  const selected = new Set(selectedKeys);
  const selectedVisibleCount = messages.filter((message) => selected.has(messageKey(message))).length;
  return (
    <div className="mt-3 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/40">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-gray-200 dark:border-gray-800">
        <div className="min-w-0">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Specific emails</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-500">
            {messages.length > 0
              ? `${selectedVisibleCount}/${messages.length} visible selected`
              : 'Optional: preview and check individual messages'}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => onSelectVisible(messages)}
                className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900"
              >
                Select visible
              </button>
              <button
                type="button"
                onClick={() => onClearVisible(messages)}
                className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900"
              >
                Clear
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onLoad}
            disabled={busy}
            className="text-[11px] px-2 py-1 rounded bg-gray-900 text-white dark:bg-gray-700 hover:bg-gray-700 disabled:opacity-50"
          >
            {busy ? 'Loading...' : messages.length > 0 ? 'Refresh' : 'Preview'}
          </button>
        </div>
      </div>
      {error && (
        <div className="px-2 py-1.5 text-xs text-rose-700 dark:text-rose-300">{error}</div>
      )}
      {messages.length > 0 && (
        <div className="max-h-56 overflow-auto divide-y divide-gray-200 dark:divide-gray-800">
          {messages.map((message) => {
            const key = messageKey(message);
            return (
              <label key={key} className="flex items-start gap-2 px-2 py-2 hover:bg-white dark:hover:bg-gray-900">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(key)}
                  onChange={(e) => onToggleMessage(message, e.target.checked)}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-medium text-gray-900 dark:text-white">
                      {message.from_name || message.from || 'Unknown sender'}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatMessageDate(message.date || message.internal_date)}
                    </span>
                  </div>
                  <div className="truncate text-xs text-gray-700 dark:text-gray-300">
                    {message.subject || '(no subject)'}
                  </div>
                  <div className="truncate text-[11px] text-gray-500 dark:text-gray-500">
                    UID {message.uid} · UIDVALIDITY {message.uidvalidity}
                    {message.message_id ? ` · ${message.message_id}` : ''}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}
      {messages.length === 500 && (
        <div className="px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
          Showing latest 500 messages in {folder.path}. Narrow recovery by selecting the specific emails visible here.
        </div>
      )}
    </div>
  );
}

function normalizeFolderPath(path: string): string {
  return path.split('/').map((segment) => segment.trim()).filter(Boolean).join('/') || 'INBOX';
}

function messageKey(message: Pick<ImapSourceMessage, 'folder' | 'uidvalidity' | 'uid'>): string {
  return `${message.folder}|${message.uidvalidity}|${message.uid}`;
}

function formatMessageDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default CreateMigrationJobWizard;
